import type { EventType } from '@/domain';

/**
 * Pure function: roll up historical orders by event_type for a given
 * (warehouse, customer). Returns per-type stats {avg, p50, p85, n}.
 *
 * Input has already been filtered to the right warehouse/customer/window
 * by the caller — engine never does I/O.
 */

export interface HistoricalSample {
  date: string;
  qty: number;
  event_type: EventType;
}

export interface DayTypeStats {
  avg: number;
  p50: number;
  p85: number;
  n: number;
  total: number;
}

export interface AggregatedHistory {
  byEventType: Map<EventType, DayTypeStats>;
  globalAvg: number;
  totalRows: number;
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0]!;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo]!;
  const w = idx - lo;
  return sorted[lo]! * (1 - w) + sorted[hi]! * w;
}

export function aggregateHistory(samples: HistoricalSample[]): AggregatedHistory {
  const buckets = new Map<EventType, number[]>();
  let total = 0;
  for (const s of samples) {
    const arr = buckets.get(s.event_type) ?? [];
    arr.push(s.qty);
    buckets.set(s.event_type, arr);
    total += s.qty;
  }
  const byEventType = new Map<EventType, DayTypeStats>();
  for (const [evt, arr] of buckets) {
    const sorted = arr.slice().sort((a, b) => a - b);
    const sum = arr.reduce((a, b) => a + b, 0);
    byEventType.set(evt, {
      avg: sum / arr.length,
      p50: percentile(sorted, 0.5),
      p85: percentile(sorted, 0.85),
      n: arr.length,
      total: sum,
    });
  }
  return {
    byEventType,
    globalAvg: samples.length > 0 ? total / samples.length : 0,
    totalRows: samples.length,
  };
}

/**
 * Resolve baseline for a target event_type with a deterministic fallback chain.
 *
 *   MEGA_SALE  → MEGA_SALE  → DOUBLE_DAY → NORMAL → globalAvg
 *   DOUBLE_DAY → DOUBLE_DAY → MINI_SPIKE → NORMAL → globalAvg
 *   PAYDAY     → PAYDAY     → MID_MONTH  → NORMAL → globalAvg
 *   ...        → same type  → NORMAL → globalAvg
 *
 * Returns the baseline stats + which type it actually came from + fallback flag.
 */
export interface BaselineResolution {
  stats: DayTypeStats;
  resolved_type: EventType;
  fallback_used: boolean;
}

const FALLBACK_CHAIN: Record<EventType, EventType[]> = {
  MEGA_SALE: ['MEGA_SALE', 'DOUBLE_DAY', 'NORMAL'],
  DOUBLE_DAY: ['DOUBLE_DAY', 'MINI_SPIKE', 'MEGA_SALE', 'NORMAL'],
  MINI_SPIKE: ['MINI_SPIKE', 'PAYDAY', 'MID_MONTH', 'NORMAL'],
  PAYDAY: ['PAYDAY', 'MID_MONTH', 'NORMAL'],
  MID_MONTH: ['MID_MONTH', 'PAYDAY', 'NORMAL'],
  NORMAL: ['NORMAL'],
};

export function resolveBaseline(
  history: AggregatedHistory,
  targetType: EventType,
): BaselineResolution {
  const chain = FALLBACK_CHAIN[targetType];
  for (let i = 0; i < chain.length; i++) {
    const t = chain[i]!;
    const stats = history.byEventType.get(t);
    if (stats && stats.n > 0) {
      return { stats, resolved_type: t, fallback_used: i > 0 };
    }
  }
  // Final fallback: synthesize from globalAvg
  return {
    stats: {
      avg: history.globalAvg,
      p50: history.globalAvg,
      p85: history.globalAvg,
      n: history.totalRows,
      total: 0,
    },
    resolved_type: 'NORMAL',
    fallback_used: true,
  };
}
